import { Link as MuiLink, Tooltip, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { com } from '@eidu/web'
import EntityLink from '../../components/data/EntityLink'
import useEntityType from '../../io/useEntityType'
import { AuditCategory, AuditEntry } from '../../io/api/audit/data/AuditEntry'
import entityTypeIdFromStringOrNull = com.eidu.sharedlib.entity.type.entityTypeIdFromStringOrNull
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId

type Props = {
  entry: AuditEntry
}

const PlainTarget = ({ targetId, historicalLabel }: { targetId: string; historicalLabel?: string | null }) => {
  const label = historicalLabel || targetId
  const showLabel = !!historicalLabel
  const text = (
    <Typography component="span" variant="inherit" fontFamily={showLabel ? undefined : 'monospace'}>
      {label}
    </Typography>
  )
  return showLabel ? (
    <Tooltip title={targetId} placement="top">
      <span>{text}</span>
    </Tooltip>
  ) : (
    text
  )
}

const SimpleLink = ({
  to,
  targetId,
  historicalLabel,
}: {
  to: string
  targetId: string
  historicalLabel?: string | null
}) => {
  const label = historicalLabel || targetId
  const showLabel = !!historicalLabel
  return (
    <Tooltip title={targetId} placement="top">
      <MuiLink component={RouterLink} to={to} underline="hover" color="inherit">
        <Typography component="span" variant="inherit" fontFamily={showLabel ? undefined : 'monospace'}>
          {label}
        </Typography>
      </MuiLink>
    </Tooltip>
  )
}

const EntityTypeTargetLink = ({ targetId, historicalLabel }: { targetId: string; historicalLabel: string | null }) => {
  const typeId = entityTypeIdFromStringOrNull(targetId)
  if (!typeId) return <SimpleLink to={`/types/${targetId}`} targetId={targetId} historicalLabel={historicalLabel} />
  return <ResolvedEntityTypeTargetLink typeId={typeId} targetId={targetId} historicalLabel={historicalLabel} />
}

const ResolvedEntityTypeTargetLink = ({
  typeId,
  targetId,
  historicalLabel,
}: {
  typeId: EntityTypeId
  targetId: string
  historicalLabel: string | null
}) => {
  const { entityType } = useEntityType(typeId)
  const liveLabel = entityType?.name || undefined
  const showHistoricalSuffix = !!liveLabel && !!historicalLabel && liveLabel !== historicalLabel
  const resolvedLabel = liveLabel || historicalLabel || undefined
  const label = showHistoricalSuffix ? `${liveLabel} (Old label: ${historicalLabel})` : resolvedLabel ?? targetId
  const fallbackToId = !resolvedLabel
  return (
    <Tooltip title={targetId} placement="top">
      <MuiLink component={RouterLink} to={`/types/${targetId}`} underline="hover" color="inherit">
        <Typography component="span" variant="inherit" fontFamily={fallbackToId ? 'monospace' : undefined}>
          {label}
        </Typography>
      </MuiLink>
    </Tooltip>
  )
}

const targetRoute = (category: AuditCategory, targetId: string): string | null => {
  switch (category) {
    case 'EntityType':
      return `/types/${targetId}`
    case 'Permission':
      return `/access-control/${targetId}`
    case 'SemanticLink':
      return `/semantics`
    default:
      return null
  }
}

const TargetLink = ({ entry }: Props) => {
  const { category, targetId, targetLabel } = entry

  if (category === 'Entity') return <EntityLink entityId={targetId} historicalLabel={targetLabel} />

  if (category === 'EntityType') return <EntityTypeTargetLink targetId={targetId} historicalLabel={targetLabel} />

  const route = targetRoute(category, targetId)
  if (!route) return <PlainTarget targetId={targetId} historicalLabel={targetLabel} />

  return <SimpleLink to={route} targetId={targetId} historicalLabel={targetLabel} />
}

export default TargetLink
